import { ExternalLink, Users, Megaphone, Sparkles } from 'lucide-react';

// Route-specific actionable starter cards. Each route gets up to 3 platforms /
// next steps and a couple of readiness tips so the score becomes immediately
// actionable rather than abstract.
const ROUTE_CONFIG = {
    'Crowdfunding-led (equity)': {
        intro:
            'Equity crowdfunding lets you raise from your community while issuing shares. UK platforms typically expect 20–30% pre-committed before launch.',
        platforms: [
            { name: 'Crowdcube', url: 'https://www.crowdcube.com', tagline: 'UK equity crowdfunding · seed–scale' },
            { name: 'Seedrs', url: 'https://www.seedrs.com', tagline: 'UK / EU equity, EIS/SEIS supported' },
            { name: 'Republic Europe', url: 'https://republic.com/europe', tagline: 'Global investor base' },
        ],
        tips: [
            'Secure 25%+ of your target as a pre-committed lead/anchor before launch',
            'Confirm SEIS/EIS advance assurance with HMRC — investors expect it',
            'Build a pre-launch list of 500+ engaged supporters before opening',
        ],
    },
    'Crowdfunding-led (reward-based)': {
        intro:
            'Reward-based crowdfunding works best for tangible products or projects with a story. Backers fund in exchange for the product, an experience or perks.',
        platforms: [
            { name: 'Kickstarter', url: 'https://www.kickstarter.com', tagline: 'Global product & creative projects' },
            { name: 'Indiegogo', url: 'https://www.indiegogo.com', tagline: 'Flexible funding goals' },
            { name: 'Crowdfunder UK', url: 'https://www.crowdfunder.co.uk', tagline: 'UK community & social good' },
        ],
        tips: [
            'A short, well-cut product/story video lifts conversion meaningfully',
            'Plan a 30-day pre-launch list build — aim for 1,000+ "notify me" signups',
            'Set a realistic minimum funding goal that covers production + fulfilment',
        ],
    },
    'Mixed route (grant + reward-based crowdfunding)': {
        intro:
            'Mission-led ventures with real community can stack grants with a reward-based crowdfunding round to amplify reach and prove demand.',
        platforms: [
            { name: 'Crowdfunder UK', url: 'https://www.crowdfunder.co.uk', tagline: 'Match-funding pots for community projects' },
            { name: 'Spacehive', url: 'https://www.spacehive.com', tagline: 'Place-based projects, often unlocks public match' },
            { name: 'Power to Change', url: 'https://www.powertochange.org.uk', tagline: 'Grants for community businesses' },
        ],
        tips: [
            'Sequence: secure grant or pledge first, then run the campaign for proof + match',
            'Highlight measurable community outcomes — these unlock match-funding pots',
            'Plan campaign launch around an event or milestone to drive momentum',
        ],
    },
    'Mixed route (equity + reward-based crowdfunding)': {
        intro:
            'A sequenced strategy: validate demand publicly via a reward campaign, then convert traction into a stronger equity raise.',
        platforms: [
            { name: 'Kickstarter', url: 'https://www.kickstarter.com', tagline: 'Stage 1 — demand validation' },
            { name: 'Crowdcube', url: 'https://www.crowdcube.com', tagline: 'Stage 2 — equity raise off proven traction' },
        ],
        tips: [
            'Run reward campaign first; use revenue + audience as proof for the equity raise',
            'Talk to a corporate lawyer about share structure before you scale the round',
        ],
    },
    'Grant-led': {
        intro:
            'Grants are non-dilutive and well-suited to mission-led, R&D, or place-based work. Highly competitive — strong narrative and outcomes matter.',
        platforms: [
            { name: 'UK Government grants', url: 'https://www.gov.uk/business-finance-support', tagline: 'Search across UK & devolved schemes' },
            { name: 'Innovate UK Smart Grants', url: 'https://iuk.ktn-uk.org/funding/', tagline: 'Innovation & R&D' },
            { name: 'National Lottery Community Fund', url: 'https://www.tnlcommunityfund.org.uk', tagline: 'Community impact projects' },
        ],
        tips: [
            'Tailor each application to the funder\'s specific outcomes — never generic',
            'Build a clear theory of change and measurable impact framework',
            'Members get tailored monthly grant matches via the Funding Update',
        ],
    },
    'Equity-led': {
        intro:
            'Equity finance suits scalable Ltd companies with a path to significant returns. Expect dilution in exchange for capital and (often) strategic support.',
        platforms: [
            { name: 'British Business Bank — investor finder', url: 'https://www.british-business-bank.co.uk/finance-options', tagline: 'Map UK equity options' },
            { name: 'UKBAA — angel networks', url: 'https://www.ukbaa.org.uk', tagline: 'UK Business Angels Association' },
        ],
        tips: [
            'Get SEIS/EIS advance assurance — investors expect it',
            'Have a 12-month pipeline of warm investor intros before launching the round',
            'A clean cap table and well-prepared data room save weeks',
        ],
    },
    'Accelerator-led': {
        intro:
            'Accelerators offer cohort-based programmes — a mix of small cheque, mentorship, network and curriculum.',
        platforms: [
            { name: 'Y Combinator', url: 'https://www.ycombinator.com', tagline: 'Global tech accelerator' },
            { name: 'Techstars', url: 'https://www.techstars.com', tagline: 'Sector-specific UK & global tracks' },
            { name: 'Bethnal Green Ventures', url: 'https://bethnalgreenventures.com', tagline: 'UK tech-for-good accelerator' },
        ],
        tips: [
            'Apply to two or three a cycle, not ten — quality of fit beats volume',
            'Be very clear what you need from the cohort (capital? customers? mentors?)',
        ],
    },
    'Loan-led': {
        intro:
            'Debt finance keeps your equity but requires repayment with interest. Best for businesses with predictable cash flow or asset-backed growth.',
        platforms: [
            { name: 'Start Up Loans (BBB)', url: 'https://www.startuploans.co.uk', tagline: 'Government-backed up to £25k' },
            { name: 'Funding Circle', url: 'https://www.fundingcircle.com', tagline: 'SME loans, fast decisions' },
            { name: 'Recovery Loan Scheme', url: 'https://www.british-business-bank.co.uk/recovery-loan-scheme', tagline: 'Scheme-backed working capital' },
        ],
        tips: [
            'Have 6–12 months of cash flow forecasts before approaching lenders',
            'Personal guarantees are common for early-stage loans — read the terms carefully',
        ],
    },
};

// Fallback for "Mixed route (grant + accelerator)" and unspecified mixes
ROUTE_CONFIG['Mixed route (grant + accelerator)'] = {
    intro: 'Stack non-dilutive grants with cohort support from an accelerator for accelerated, capital-efficient growth.',
    platforms: [
        { name: 'Innovate UK Smart Grants', url: 'https://iuk.ktn-uk.org/funding/', tagline: 'R&D and innovation' },
        { name: 'Bethnal Green Ventures', url: 'https://bethnalgreenventures.com', tagline: 'UK tech-for-good accelerator' },
    ],
    tips: [
        'Sequence grant → accelerator so each round signals momentum to the next',
    ],
};

ROUTE_CONFIG['Mixed route'] = {
    intro: 'Your situation has signals across several routes. A blended approach often outperforms a single-channel raise.',
    platforms: [],
    tips: [
        'Consider talking through options on a member deep-dive before you commit to one route',
    ],
};

export default function RouteCard({ route }) {
    const config = ROUTE_CONFIG[route] || ROUTE_CONFIG['Mixed route'];
    const isCrowdfunding = route.toLowerCase().includes('crowdfunding');

    return (
        <div
            className="bg-white organic-border border border-weave-border p-6 md:p-8"
            data-testid="route-card"
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-weave-sand flex items-center justify-center text-weave-ink shrink-0">
                    {isCrowdfunding ? <Users className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                </div>
                <div>
                    <div className="text-overline text-weave-teal">Your likely route</div>
                    <h3 className="font-display text-2xl text-weave-ink">{route}</h3>
                </div>
            </div>
            <p className="text-sm text-weave-ink-soft leading-relaxed mb-6" data-testid="route-card-intro">
                {config.intro}
            </p>

            {config.platforms.length > 0 && (
                <div className="mb-6">
                    <div className="text-overline text-weave-muted mb-3">Where to start</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {config.platforms.map((p) => (
                            <a
                                key={p.name}
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-start justify-between gap-2 p-4 rounded-xl border border-weave-border bg-[#f8f4ec] hover:bg-white hover:border-weave-teal transition"
                                data-testid={`route-platform-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-display text-base text-weave-ink truncate">{p.name}</div>
                                    <div className="text-xs text-weave-muted truncate">{p.tagline}</div>
                                </div>
                                <ExternalLink className="w-4 h-4 text-weave-muted group-hover:text-weave-teal shrink-0 mt-1" />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {config.tips.length > 0 && (
                <div>
                    <div className="text-overline text-weave-muted mb-3 flex items-center gap-2">
                        <Megaphone className="w-3 h-3" /> Readiness tips
                    </div>
                    <ul className="space-y-2 text-sm text-weave-ink-soft">
                        {config.tips.map((tip, i) => (
                            <li key={i} className="flex gap-2 items-start" data-testid={`route-tip-${i}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-weave-teal mt-2 shrink-0" />
                                <span className="leading-relaxed">{tip}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-6 text-xs text-weave-muted leading-relaxed">
                Suggestions are educational — not endorsements or regulated advice. Always
                check current terms with each platform.
            </div>
        </div>
    );
}
